import i from '../../../translations/i'
import { customToFixed } from '../../../helpers/project.controller'
import { storeLocal } from '../../../helpers/stores'

/** Пороги совпадают с App\Domain\Cart\Enums\CartActivity на сервере */
export const ACTIVE_MINUTES = 30
export const ABANDONED_HOURS = 24

/** Как часто обновляются список и карточка в live-режиме */
export const LIVE_INTERVAL_MS = 15000

/** Сколько держится подсветка строки, которая поменялась с прошлого обновления */
export const FLASH_MS = 2500

export type ActivityState = 'active' | 'idle' | 'abandoned' | 'unknown'

export const activityState = (iso: string | null): ActivityState => {
    if (!iso) return 'unknown'

    const minutes = (Date.now() - new Date(iso).getTime()) / 60000
    if (minutes <= ACTIVE_MINUTES) return 'active'
    if (minutes > ABANDONED_HOURS * 60) return 'abandoned'

    return 'idle'
}

export const timeAgo = (iso: string | null): string => {
    if (!iso) return '—'

    const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
    if (seconds < 60) return i.t('carts:time.justNow')
    if (seconds < 3600) return i.t('carts:time.minutes', { n: Math.floor(seconds / 60) })
    if (seconds < 86400) return i.t('carts:time.hours', { n: Math.floor(seconds / 3600) })

    return i.t('carts:time.days', { n: Math.floor(seconds / 86400) })
}

export const formatDateTime = (iso: string | null): string => iso
    ? new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(iso))
    : '—'

export const formatTime = (date: Date | null): string => date
    ? new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(date)
    : ''

/** Телефоны лежат цифрами: 79991234567 → +7 999 123-45-67 */
export const formatPhone = (phone: string | null): string => {
    if (!phone) return ''

    const digits = phone.replace(/\D+/g, '')
    if (digits.length === 11 && (digits[0] === '7' || digits[0] === '8')) {
        return `+7 ${digits.slice(1, 4)} ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`
    }

    return digits.length > 0 ? `+${digits}` : phone
}

export const userName = (user: any): string => {
    if (!user) return '—'
    if (user.name) return user.name

    return `${user.registered ? i.t('carts:user.noName') : i.t('carts:user.guest')} #${user.id}`
}

/**
 * В корзине лежит количество шагов товара, а людям показываем количество в единицах:
 * 3 шага по 100 г — это 300 г. Так же считает карточка заказа.
 */
export const toDisplay = (count: number, step: number): number => Number(customToFixed(count * (step || 1), 3))

export const fromDisplay = (value: number, step: number): number => value / (step || 1)

/** Способ получения: CartController::setDelivery() пишет в корзину что прислали, поэтому незнакомое показываем как есть */
export const deliveryLabel = (delivery: string | null): string => {
    if (!delivery) return i.t('carts:delivery.none')
    if (delivery === 'delivery' || delivery === 'pickup' || delivery === 'default') {
        return i.t(`carts:delivery.${delivery === 'default' ? 'delivery' : delivery}`)
    }

    return delivery
}

/** Текст ошибки из ответа открытого API: {data: {message}} */
export const errorMessage = (err: any): string => err?.response?.data?.data?.message ?? i.t('carts:errors.common')

/**
 * Администратор панели: роли нет у владельца проекта, 0 — администратор, 5 — администратор Lite.
 * Тот же признак, что у пунктов меню без ролей в Navigation.
 */
export const isAdminRole = (): boolean => {
    const role = storeLocal.get('role')

    return !role || Number(role) === 5
}

/** Подписи выбранных в фильтре товара, склада и типов цен: сам фильтр хранит только id */
export interface CartsFilterLabels {
    product?: string
    warehouse?: string
    priceTypes?: string
}

/** Фильтры списка корзин вместе с подписями — так их передают на страницу выгрузки и в редактор пуша */
export interface CartsSegment {
    filters: Record<string, any>
    labels: CartsFilterLabels
}

/** Фильтры со списком значений: в API уходят как filter[price_types][]=… */
const LIST_FILTERS = ['price_types']
export const isCartsListFilter = (key: string) => LIST_FILTERS.includes(key)

export const isCartsFilterEmpty = (value: any): boolean =>
    value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)

/** Только заданные фильтры, парами «ключ — значение»; у фильтров-списков значение — список строк */
export const cartsFilterEntries = (filters: Record<string, any>): [string, string | string[]][] => Object.entries(filters ?? {})
    .filter(([, value]) => !isCartsFilterEmpty(value))
    .map(([key, value]) => [key, Array.isArray(value) ? value.map(item => `${item}`) : `${value}`])

/** Параметры запроса парами: filter[warehouse_id]=…, у списков — filter[price_types][]=… на каждое значение */
export const cartsFilterParams = (filters: Record<string, any>, prefix: string = 'filter'): [string, string][] => cartsFilterEntries(filters)
    .flatMap(([key, value]): [string, string][] => Array.isArray(value)
        ? value.map(item => [`${prefix}[${key}][]`, item])
        : [[`${prefix}[${key}]`, value]])

/** Параметры для API: filter[activity]=…&filter[warehouse_id]=… */
export const cartsFilterQuery = (filters: Record<string, any>, prefix: string = 'filter'): string => cartsFilterParams(filters, prefix)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&')

/** Значение параметра carts в адресе страницы выгрузки и редактора пуша */
export const cartsSegmentParam = (filters: Record<string, any>, labels: CartsFilterLabels): string =>
    encodeURIComponent(JSON.stringify({ filters: Object.fromEntries(cartsFilterEntries(filters)), labels }))

export const parseCartsSegment = (raw: string | null): CartsSegment | null => {
    if (!raw) return null

    try {
        const parsed = JSON.parse(raw)

        return { filters: parsed?.filters ?? {}, labels: parsed?.labels ?? {} }
    } catch (error) {
        console.log(error)

        return null
    }
}

const formatDay = (unix: string | number): string => new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(new Date(Number(unix) * 1000))

/** Условия фильтра человеческими словами; пустой список — все корзины с товарами */
export const cartsFilterChips = ({ filters, labels }: CartsSegment): string[] => {
    const has = (key: string) => !isCartsFilterEmpty(filters[key])
    const chips: string[] = []

    if (has('activity')) chips.push(i.t(`carts:activity.${filters.activity}`))
    if (has('activity_from') || has('activity_to')) {
        chips.push(i.t('carts:segment.activityRange', {
            from: has('activity_from') ? formatDay(filters.activity_from) : '…',
            to: has('activity_to') ? formatDay(filters.activity_to) : '…',
        }))
    }
    if (has('warehouse_id')) chips.push(i.t('carts:segment.warehouse', { name: labels.warehouse ?? `#${filters.warehouse_id}` }))
    if (has('price_types')) {
        const ids: any[] = Array.isArray(filters.price_types) ? filters.price_types : [filters.price_types]
        chips.push(i.t('carts:segment.priceTypes', { list: labels.priceTypes ?? ids.map(id => `#${id}`).join(', ') }))
    }
    if (has('product_id')) chips.push(i.t('carts:segment.product', { name: labels.product ?? `#${filters.product_id}` }))
    if (has('sum_from') || has('sum_to')) {
        chips.push(i.t('carts:segment.sum', { from: has('sum_from') ? filters.sum_from : '0', to: has('sum_to') ? filters.sum_to : '∞' }))
    }
    if (has('registered')) chips.push(i.t(Number(filters.registered) ? 'carts:filters.registered' : 'carts:filters.guests'))
    if (has('has_promo_code')) chips.push(i.t(Number(filters.has_promo_code) ? 'carts:filters.withPromo' : 'carts:filters.withoutPromo'))
    if (has('delivery')) chips.push(deliveryLabel(filters.delivery))
    if (has('search')) chips.push(i.t('carts:segment.search', { value: filters.search }))

    return chips
}
